/** SUNTRONIX ONE — Customer Prefix Cleanup.
 *  Finds customers whose legal name still starts with M/s./Mr./Dr./… and moves
 *  that prefix/title into the structured display field. Preview before saving.
 */
import { useEffect, useState } from "react";
import { Wand2, Loader2, ArrowRight, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

export default function CustomerPrefixCleanup() {
  const [rows, setRows] = useState(null);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);

  const scan = () => {
    setRows(null); setSel({});
    api.get("/vtiger/crm-customers/prefix-scan")
      .then(({ data }) => { setRows(data.items || []); setSel(Object.fromEntries((data.items || []).map((r) => [r.id, true]))); })
      .catch((e) => { toast.error(e?.response?.data?.detail || "Scan failed"); setRows([]); });
  };
  useEffect(() => { scan(); }, []);

  const chosen = rows ? rows.filter((r) => sel[r.id]) : [];

  const apply = async () => {
    if (!chosen.length) return toast.error("Select at least one customer");
    setBusy(true);
    try {
      const { data } = await api.post("/vtiger/crm-customers/prefix-apply", { ids: chosen.map((r) => r.id) });
      toast.success(`Updated ${data.updated} customer(s)`);
      scan();
    } catch (e) { toast.error(e?.response?.data?.detail || "Apply failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="prefix-cleanup-page">
      <div className="text-[11px] uppercase tracking-widest text-indigo-700 font-semibold">Configuration Studio · Customers</div>
      <h1 className="text-2xl font-heading font-bold flex items-center gap-2"><Wand2 className="h-6 w-6 text-indigo-600" /> Prefix &amp; Title Cleanup</h1>
      <p className="text-sm text-slate-500 mt-1">Move any "M/s." / "Mr." / "Dr." that is currently part of a customer's name into the proper display field. The legal name (used for GST/PAN/invoice matching) is corrected; nothing is deleted. Review before applying.</p>

      <div className="mt-4 flex items-center justify-between">
        <Button variant="outline" onClick={scan} data-testid="prefix-rescan-btn">Re-scan</Button>
        <div className="flex items-center gap-3">
          {rows && <span className="text-[12px] text-slate-500">{chosen.length} selected</span>}
          <Button onClick={apply} disabled={busy || !chosen.length} className="bg-indigo-700" data-testid="prefix-apply-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <ShieldCheck className="h-4 w-4 mr-1.5" />} Apply selected
          </Button>
        </div>
      </div>

      {rows === null ? (
        <div className="flex items-center gap-2 text-slate-500 mt-8"><Loader2 className="h-4 w-4 animate-spin" /> Scanning customers…</div>
      ) : rows.length === 0 ? (
        <div className="text-center text-slate-500 border border-dashed rounded-lg py-16 mt-6" data-testid="prefix-empty">
          All clean — no customer names start with a prefix or title.
        </div>
      ) : (
        <div className="mt-4 border rounded-lg divide-y" data-testid="prefix-list">
          {rows.map((r) => (
            <label key={r.id} className="flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 cursor-pointer" data-testid={`prefix-row-${r.id}`}>
              <Checkbox checked={!!sel[r.id]} onCheckedChange={(v) => setSel((s) => ({ ...s, [r.id]: !!v }))} data-testid={`prefix-check-${r.id}`} />
              <div className="flex-1 min-w-0 grid grid-cols-[1fr_auto_1fr] items-center gap-3 text-[13px]">
                <div className="truncate">
                  <span className="text-slate-400 text-[11px] mr-1">{r.customer_code}</span>
                  <span className="line-through text-slate-400">{r.current_name}</span>
                </div>
                <ArrowRight className="h-4 w-4 text-slate-400" />
                <div className="truncate">
                  <span className="font-semibold text-slate-900">{r.proposed_legal_name}</span>
                  <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-800">
                    {r.field === "contact_title" ? "Title" : "Prefix"}: {r.proposed_value}
                  </span>
                </div>
              </div>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
